import { useInterval } from 'ahooks'
import { useCallback, useEffect, useState } from 'react'

import { API } from '@/api'
import { Trans } from '@/components'
import { Loading } from '@/components/base/Loading'
import { MachineBill } from '@/models/bill'
import { Instance } from '@/models/instance'
import { formatDatePreset, formatPrice } from '@/utils/formatter'

interface InstanceBillingProps {
  instance: Instance
}

export function InstanceBilling({ instance }: InstanceBillingProps) {
  const [bills, setBills] = useState<MachineBill[]>([])
  const [loading, setLoading] = useState(false)

  const getData = useCallback(async () => {
    setLoading(true)
    const res = await API.getNodeBill({ request_id: instance.displayID })
    if (res.code === 0) {
      setBills(res.bills.map((bill) => new MachineBill(bill)))
    }
    setLoading(false)
  }, [instance])

  useInterval(
    () => {
      getData()
    },
    instance.isReleased ? undefined : 10000,
    { immediate: true },
  )

  useEffect(() => {
    if (instance.isReleased) {
      getData()
    }
  }, [instance.displayID, instance.isReleased])

  return (
    <div className="bg-panel-gray-100 rounded-lg flex flex-col py-4 t-sm text-tertiary gap-y-4">
      {loading ? (
        <div className="w-full flex items-center justify-center">
          <Loading />
        </div>
      ) : (
        bills.map((bill) => (
          <div key={bill.displayID} className="flex w-full justify-between">
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.startTime">Start Time</Trans>
              </div>
              <div className="text-primary">{formatDatePreset(bill.startTime, 'minute')}</div>
            </div>
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.endTime">End Time</Trans>
              </div>
              <div className="text-primary">
                {bill.isRunning ? (
                  <div className="text-success">Running</div>
                ) : (
                  formatDatePreset(bill.endTime, 'minute')
                )}
              </div>
            </div>
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.price">Price</Trans>
              </div>
              <div className="text-primary">
                {formatPrice(bill.price, false, bill.price > 100 ? 'Credits' : 'Credit')}
              </div>
            </div>
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.cost">Cost</Trans>
              </div>
              <div className="text-primary">
                {formatPrice(
                  Math.abs(bill.amount),
                  false,
                  Math.abs(bill.amount) > 100 ? 'Credits' : 'Credit',
                )}
              </div>
            </div>
            <div className="px-4">
              <div className="text-secondary">
                <Trans i18nKey="instance.detail.billing.duration">Duration</Trans>
              </div>
              <div className="text-primary">{bill.usedTimeStr}</div>
            </div>
          </div>
        ))
      )}
    </div>
  )
}
